import type { PoS } from '../types';
import type { FollowupPriority, InterestBaseline } from './followupPriority';
import type { OrderItem } from '../components/visits/OrderCollectionForm';

export const ACTIVE_VISIT_EVENT = 'bc-crm-active-visit';

export const DRAFT_KEY = (userId: string) => `bc-crm.active-visit.${userId}`;
export const LAST_VISITS_PATH_KEY = (userId: string) =>
  `bc-crm.last-visits-path.${userId}`;
export const OPEN_VISIT_ID_KEY = (userId: string) =>
  `bc-crm.open-visit-id.${userId}`;

export interface VisitWizardDraft {
  version: 1;
  step: number;
  posMode: 'existing' | 'new';
  selectedPos: PoS | null;
  isNewPos: boolean;
  selectedProductIds: string[];
  productPlacements: Record<string, unknown>;
  expandedProduct: string | null;
  salesExpanded: boolean;
  salesPerformance: Record<
    string,
    { units_sold: number; current_stock: number; rating: string; notes: string }
  >;
  otherOutcome: string;
  orders: OrderItem[];
  ordersExpanded: boolean;
  followupExpanded: boolean;
  notesExpanded: boolean;
  visitNotes: string;
  notesPinned: boolean;
  sendFollowupEmail: boolean;
  followupEmailRecipient: string;
  followupEmailLanguage: 'de' | 'en';
  needsFollowup: boolean;
  followupMaterials: string[];
  followupActions: string[];
  followupNotes: string;
  followupPriority: FollowupPriority;
  interestBaseline?: InterestBaseline | null;
  /** Unsaved New PoS form. Absent on drafts saved before this field existed. */
  newPosForm?: NewPosFormDraft | null;
  newPosDistributors?: NewPosDistributorDraft[];
}

export interface NewPosFormDraft {
  name: string;
  store_type: string;
  street: string;
  street_number: string;
  postal_code: string;
  city: string;
  country: string;
  district: string;
  phone: string;
  email: string;
  invoice_email: string;
  owner_name: string;
  invoice_name: string;
  invoice_street: string;
  invoice_street_number: string;
  invoice_postal_code: string;
  invoice_city: string;
  invoice_country: string;
  contact_person_name: string;
  contact_person_phone: string;
  contact_person_email: string;
  contact_availability_time: string;
  estimated_traffic: string;
  notes: string;
  tax_number: string;
  vat_id: string;
  is_premium: boolean;
}

export interface NewPosDistributorDraft {
  distributor_id?: string;
  pos_number_at_distributor?: string;
  is_primary?: boolean;
}

export const NEW_POS_FORM_DEFAULTS: NewPosFormDraft = {
  name: '',
  store_type: 'spaeti',
  street: '',
  street_number: '',
  postal_code: '',
  city: 'Berlin',
  country: 'Germany',
  district: '',
  phone: '',
  email: '',
  invoice_email: '',
  owner_name: '',
  invoice_name: '',
  invoice_street: '',
  invoice_street_number: '',
  invoice_postal_code: '',
  invoice_city: '',
  invoice_country: '',
  contact_person_name: '',
  contact_person_phone: '',
  contact_person_email: '',
  contact_availability_time: '',
  estimated_traffic: '',
  notes: '',
  tax_number: '',
  vat_id: '',
  is_premium: false,
};

function sameAsDefault(key: keyof NewPosFormDraft, value: unknown): boolean {
  const fallback = NEW_POS_FORM_DEFAULTS[key];
  if (typeof fallback === 'boolean') return value === fallback;
  return String(value ?? '').trim() === fallback;
}

/** True once the user changed something other than the empty Späti/Berlin defaults. */
export function toNewPosFormDraft(
  values: Partial<NewPosFormDraft> | null | undefined
): NewPosFormDraft {
  const next: NewPosFormDraft = { ...NEW_POS_FORM_DEFAULTS };
  if (!values) return next;
  for (const key of Object.keys(NEW_POS_FORM_DEFAULTS) as Array<keyof NewPosFormDraft>) {
    const value = values[key];
    if (value === undefined || value === null) continue;
    if (key === 'is_premium') {
      next.is_premium = Boolean(value);
      continue;
    }
    next[key] = String(value) as NewPosFormDraft[typeof key];
  }
  return next;
}

export function isNewPosFormStarted(
  form: NewPosFormDraft | null | undefined,
  distributorCount = 0
): boolean {
  if (distributorCount > 0) return true;
  if (!form) return false;
  return (Object.keys(NEW_POS_FORM_DEFAULTS) as Array<keyof NewPosFormDraft>).some(
    (key) => !sameAsDefault(key, form[key])
  );
}

export interface ActiveVisitMeta {
  hasDraft: boolean;
  label: string;
}

export function isMeaningfulDraft(draft: VisitWizardDraft | null): boolean {
  if (!draft) return false;
  if (draft.selectedPos) return true;
  if (draft.selectedProductIds.length > 0) return true;
  if (draft.orders.length > 0) return true;
  if (draft.visitNotes.trim() !== '') return true;
  if (draft.step > 0) return true;
  if (isNewPosFormStarted(draft.newPosForm, draft.newPosDistributors?.length ?? 0)) {
    return true;
  }
  return false;
}

export function draftLabel(draft: VisitWizardDraft | null): string {
  const name = draft?.selectedPos?.name?.trim() || draft?.newPosForm?.name?.trim();
  return name || 'New visit';
}

export function peekActiveVisit(userId: string): ActiveVisitMeta {
  const draft = loadActiveVisitDraft(userId);
  if (!isMeaningfulDraft(draft)) {
    return { hasDraft: false, label: 'New visit' };
  }
  return { hasDraft: true, label: draftLabel(draft) };
}

export function loadActiveVisitDraft(userId: string): VisitWizardDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VisitWizardDraft;
    if (parsed?.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveActiveVisitDraft(
  userId: string,
  draft: VisitWizardDraft
): void {
  try {
    localStorage.setItem(DRAFT_KEY(userId), JSON.stringify(draft));
    notifyActiveVisitChanged();
  } catch {
    // Quota or private mode — FAB simply stays hidden.
  }
}

export function clearActiveVisitDraft(userId: string): void {
  try {
    localStorage.removeItem(DRAFT_KEY(userId));
  } catch {
    // ignore
  }
  notifyActiveVisitChanged();
}

export function notifyActiveVisitChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(ACTIVE_VISIT_EVENT));
}

export function getLastVisitsPath(userId: string): string {
  try {
    const path = sessionStorage.getItem(LAST_VISITS_PATH_KEY(userId));
    if (path === '/visits' || path === '/visits/new') return path;
  } catch {
    // ignore
  }
  return '/visits';
}

export function setLastVisitsPath(userId: string, path: string): void {
  if (path !== '/visits' && path !== '/visits/new') return;
  try {
    sessionStorage.setItem(LAST_VISITS_PATH_KEY(userId), path);
  } catch {
    // ignore
  }
}

export function getOpenVisitId(userId: string): string | null {
  try {
    return sessionStorage.getItem(OPEN_VISIT_ID_KEY(userId));
  } catch {
    return null;
  }
}

export function setOpenVisitId(userId: string, visitId: string | null): void {
  try {
    if (!visitId) sessionStorage.removeItem(OPEN_VISIT_ID_KEY(userId));
    else sessionStorage.setItem(OPEN_VISIT_ID_KEY(userId), visitId);
  } catch {
    // ignore
  }
}
